/**
 * `/shared/:id` — public read of a share-link snapshot.
 *
 * No auth required. The view is immutable — no Run check / Run summary /
 * share-link buttons. Otherwise the layout (header → summary strip → card
 * list with verdict rail) mirrors `DocumentPage` so a shared snapshot looks
 * like the original document workspace.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DEFAULT_USER_PREFERENCES, type ShareAnalysisSnapshot } from '@eloquor/api-contract';
import { readShareAnalysis } from '../api/analyses';
import { AuthShell } from '../components/layout/AuthShell';
import { CardList } from '../components/cards/CardRow';
import { SummaryStrip } from '../components/cards/SummaryStrip';
import { ApiCallError } from '../api/client';
import { printPdfViaIframe } from '../report/rendering';

export function SharedAnalysisPage() {
  const { id } = useParams<{ id: string }>();
  const [snap, setSnap] = useState<ShareAnalysisSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        setSnap(await readShareAnalysis(id));
      } catch (err) {
        if (err instanceof ApiCallError && err.status === 410) {
          setError('This share link has expired.');
        } else if (err instanceof ApiCallError && err.status === 404) {
          setError('This share link is not recognized.');
        } else {
          setError('Could not load this snapshot.');
        }
      }
    })();
  }, [id]);

  return (
    <AuthShell>
      <div style={{ width: '100%', maxWidth: 1240, padding: '0 32px' }}>
        {error ? (
          <div className="banner danger">{error}</div>
        ) : !snap ? (
          <div style={{ color: 'var(--fg-3)' }}>Loading…</div>
        ) : (
          <>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <div>
                <div className="auth-eyebrow">
                  <span className="num">SHARED</span> · Read-only
                </div>
                <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 4px' }}>
                  {snap.file.originalName}
                </h1>
                <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
                  {snap.cards.length} cards · captured{' '}
                  {new Date(snap.createdAt).toLocaleDateString()} · expires{' '}
                  {new Date(snap.expiresAt).toLocaleDateString()}
                </div>
              </div>
            </header>

            <SummaryStrip summary={snap.summary} />

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
              <button
                className="btn-ghost"
                type="button"
                onClick={() => printPdfViaIframe({ cards: snap.cards, summary: snap.summary })}
              >
                Export PDF
              </button>
            </div>

            <CardList
              cards={snap.cards}
              highlightColor={DEFAULT_USER_PREFERENCES.highlightColor}
            />
          </>
        )}
      </div>
    </AuthShell>
  );
}
